// Composer Studio — 조립/수동 데이터 소스(dataSources) 칼럼 편집 순수 연산 (동적 데이터셋 통합 D3)
//
// 셀 바인딩이 정본, 선언 fields는 자동 동기(2026-07-04 사용자 확정). 반복영역 편집기의
// "＋칼럼 추가" / 키 rename / 칼럼 삭제 흐름이 쓰는 순수 template 변환들이다. 각 연산은
// 셀 바인딩 갱신(updateCell)과 선언 fields 갱신을 **한 번의 반환**으로 묶어
// 호출부가 `apply((t) => op(t, …))` 단일 producer(=undo 1스텝)로 쓸 수 있게 한다.
// "선언이 곧 행 데이터셋"(D1) — over가 dsKey인 반복영역의 칼럼 편집을 담당한다.
// (구 작성폼 행 컬렉션(widget:'rows') 버전 연산들은 D4-1에서 제거 — dataSource 경로가 정본.)

import type {
  Binding,
  BindingLifecycle,
  ComposerNode,
  DataSourceFieldMap,
  ReportTemplate,
} from '@safetyhub/shared';
import { updateCell, updateRepeatRegion } from '@safetyhub/report-composer';
import { findNodeById } from './findNodeById';
import { walkNodeTree } from './nodeTreeWalk';

/**
 * regionIndex 반복영역의 밴드 행 범위 [min,max]를 반환한다(표/영역 부재 시 null).
 * "＋칼럼 추가" 픽 좌표(row)가 이 범위 안인지 방어 검증하는 데 쓴다.
 */
function bandRowRange(
  t: ReportTemplate,
  tableId: string,
  regionIndex: number,
): [number, number] | null {
  const table = findNodeById(t.body, tableId);
  if (table?.kind !== 'table') return null;
  const region = (table.repeatRegions ?? [])[regionIndex];
  if (!region) return null;
  return [Math.min(region.rowStart, region.rowEnd), Math.max(region.rowStart, region.rowEnd)];
}

/**
 * 조립 데이터 소스(dataSources) 필드 키 rename(템플릿 전역 참조 동기) — DataSourcesEditor 필드 키 편집이 사용.
 * dsKey 선언의 fields[].field(oldField→newField)를 바꾸고, 그 소스를 소비하는 참조를 함께 동기한다
 * (한 번의 반환=undo 1스텝) — "정본-파생 한 스텝 동기" 원칙.
 *   a. `t.dataSources`의 dsKey 선언 fields[].field: oldField→newField. `from`은 원천 경로라 불변.
 *   b. **직접 반복 표**: `region.over===dsKey`인 표의 `binding.bindingKey===oldField` 셀 rename
 *      (format 등 나머지 binding 보존 — 좌표 선수집 후 updateCell 순차 적용).
 *   c. oldField===newField 또는 dsKey 부재면 원본 참조 반환(no-op 히스토리 방지).
 * ⚠️ 셀 format 문자열 안의 경로(`{oldField.likelihood}` 등)는 자동 rewrite하지 않는다(문자열 치환 위험).
 *    감지·경고는 호출부(DataSourcesEditor 확정 핸들러) 책임이다.
 */
export function renameDataSourceFieldEverywhere(
  t: ReportTemplate,
  params: { dsKey: string; oldField: string; newField: string },
): ReportTemplate {
  const { dsKey, oldField, newField } = params;
  if (oldField === newField) return t;
  const dataSources = t.dataSources ?? [];
  const dsIndex = dataSources.findIndex((d) => d.key === dsKey);
  if (dsIndex === -1) return t;
  let next = t;

  // a) 선언 필드 키 rename(from은 불변).
  const def = dataSources[dsIndex];
  if ((def.fields ?? []).some((f) => f.field === oldField)) {
    const nextDefs = dataSources.map((d, i) =>
      i === dsIndex
        ? {
            ...d,
            fields: d.fields.map((f) => (f.field === oldField ? { ...f, field: newField } : f)),
          }
        : d,
    );
    next = { ...next, dataSources: nextDefs };
  }

  // b) 직접 반복 표(region.over===dsKey)의 oldField 셀 바인딩 rename.
  //    좌표(tableId,row,col)를 먼저 수집한 뒤 updateCell로 순차 적용(좌표는 안정 식별자라 next 진화와 무관).
  const targets: { tableId: string; row: number; col: number; binding: Binding }[] = [];
  walkNodeTree(next.body, (n) => {
    if (n.kind !== 'table') return;
    if (!(n.repeatRegions ?? []).some((r) => r.over === dsKey)) return;
    for (const c of n.cells) {
      if (c.binding && c.binding.bindingKey === oldField) {
        targets.push({ tableId: n.id, row: c.row, col: c.col, binding: c.binding });
      }
    }
  });
  for (const tg of targets) {
    next = updateCell(next, tg.tableId, tg.row, tg.col, {
      binding: { ...tg.binding, bindingKey: newField },
    });
  }

  return next;
}

/** 트리에서 dsKey인 dataSources 선언 인덱스를 찾는다(없으면 -1). */
function findDataSourceIndex(t: ReportTemplate, dsKey: string): number {
  return (t.dataSources ?? []).findIndex((d) => d.key === dsKey);
}

/**
 * 칼럼 매핑 **특례 자동 생성** 셀 콘텐츠 판정 — content가 규약 id(`image-N`/`choicegroup-N`,
 * buildImageNodeForField/buildChoiceGroupNodeForField 발급) **단일 노드**이고 그 노드 binding이
 * 셀의 매핑 필드(bindingKey)와 일치할 때만 true. 재지정/칼럼 삭제 시 이 판정이 참일 때만
 * content를 함께 비운다 — content>binding 렌더 우선순위라 특례 잔존이 유령 이미지/선택지로
 * 남는 것을 막되, 손저작 콘텐츠(다른 id·다중 노드·다른 binding)는 절대 지우지 않는다.
 */
export function isAutoFieldCellContent(
  content: ComposerNode[] | undefined,
  bindingKey: string | undefined,
): boolean {
  if (!content || content.length !== 1 || !bindingKey) return false;
  const node = content[0];
  if (!/^(image|choicegroup)-\d+$/.test(node.id)) return false;
  return (
    (node as { binding?: { bindingKey?: string } }).binding?.bindingKey === bindingKey
  );
}

/**
 * (dataSource "＋칼럼 추가") 픽한 셀에 새 필드 키를 바인딩하고, 그 선언(dsKey)의 fields에 필드를
 * append한다(이미 있으면 append 생략 — 미매핑 선언 필드를 셀에 붙이는 경우). 반복영역 칼럼 범위도
 * 바인딩된 필드 셀들로 재계산해 새 칼럼이 밴드에 포함되게 한다. 모두 한 번의 반환(단일 undo 스텝).
 *   - 셀: binding.bindingKey = field(lifecycle 지정), 정적 text 제거
 *   - 선언 fields: { field, label, from, widget:'text', editable:true } append
 *     (from 미지정 = '' — 수동 데이터셋 관례. 조립형에서 조회 경로를 골라 추가하면 그 경로가 온다)
 */
export function addDataSourceColumn(
  t: ReportTemplate,
  params: {
    tableId: string;
    regionIndex: number;
    row: number;
    col: number;
    dsKey: string;
    field: string;
    label: string;
    lifecycle: BindingLifecycle;
    /** 조립형 소스에서 조회 경로를 골라 추가할 때의 값 출처(예 'primary.locationName'). 기본 ''. */
    from?: string;
  },
): ReportTemplate {
  const { tableId, regionIndex, row, col, dsKey, field, label, lifecycle } = params;
  // 방어: 픽 좌표(row)가 이 반복영역 밴드 행 범위 밖이면 바인딩/재계산을 적용하지 않는다(원본 참조 반환).
  // 밴드 밖 템플릿 셀에 선언 필드를 바인딩하면 칼럼범위 재계산과 맞물려 표 레이아웃이 붕괴한다(2026-07-05).
  const band = bandRowRange(t, tableId, regionIndex);
  if (band && (row < band[0] || row > band[1])) return t;
  // 1) 셀 바인딩 설정(정적 text 제거).
  let next = updateCell(t, tableId, row, col, {
    binding: { bindingKey: field, lifecycle },
    text: undefined,
  });
  // 2) 선언 fields append(이미 있으면 생략 — 미매핑 선언 필드를 셀에 붙이는 케이스).
  const dsIndex = findDataSourceIndex(next, dsKey);
  if (dsIndex !== -1) {
    const dataSources = next.dataSources ?? [];
    const def = dataSources[dsIndex];
    if (!(def.fields ?? []).some((f) => f.field === field)) {
      const newField: DataSourceFieldMap = {
        field,
        label,
        from: params.from ?? '',
        widget: 'text',
        editable: true,
      };
      next = {
        ...next,
        dataSources: dataSources.map((d, i) =>
          i === dsIndex ? { ...d, fields: [...(d.fields ?? []), newField] } : d,
        ),
      };
    }
  }
  // 3) 영역 칼럼 범위 — 새 칼럼이 밴드 밖이면 포함하도록 **확장만** 한다(축소 금지).
  //    구 min/max 재계산은 첫 칼럼 픽 시 밴드 폭을 그 칼럼 하나로 축소시켜, 실데이터 전개에서
  //    나머지 칼럼이 밴드 밖으로 밀리는 표 붕괴를 유발했다(2026-07-05 실사고).
  //    명시 범위가 없으면(전폭) 이미 포함이므로 그대로 둔다.
  const table = findNodeById(next.body, tableId);
  if (table?.kind === 'table') {
    const region = (table.repeatRegions ?? [])[regionIndex];
    if (region && region.colStart !== undefined && region.colEnd !== undefined) {
      const colStart = Math.min(region.colStart, col);
      const colEnd = Math.max(region.colEnd, col);
      if (colStart !== region.colStart || colEnd !== region.colEnd) {
        next = updateRepeatRegion(next, tableId, regionIndex, { colStart, colEnd });
      }
    }
  }
  return next;
}

/**
 * 조립/수동 데이터 소스 필드 **삭제**(템플릿 전역 참조 동기) — DataSourcesEditor 필드 삭제가 쓴다.
 *
 * rename(renameDataSourceFieldEverywhere)은 선언과 표 셀을 함께 바꾸는데 삭제는 선언만 지우고
 * 있었다. 그러면 표에는 **선언에 없는 키를 가리키는 바인딩**이 남아, 화면에서는 빈 칸인데
 * 칼럼 목록에도 없는 유령이 된다(2026-08-11 확인). 두 연산의 파급 범위를 맞춘다.
 *
 *   a. `region.over===dsKey`인 표에서 `binding.bindingKey===field`인 셀의 바인딩 해제
 *      (특례 자동 생성 콘텐츠는 함께 비움 — clearFieldCellBinding과 같은 규약)
 *   b. dsKey 선언 fields에서 그 필드 제거
 *   c. dsKey 부재면 표 정리만 수행. 바꿀 것이 없으면 원본 참조 반환(no-op 히스토리 방지)
 */
export function deleteDataSourceFieldEverywhere(
  t: ReportTemplate,
  params: { dsKey: string; field: string },
): ReportTemplate {
  const { dsKey, field } = params;
  let next = t;

  // a) 이 소스를 반복하는 모든 표의 해당 바인딩 해제(좌표 선수집 후 순차 적용 — rename과 같은 결).
  const tableIds: string[] = [];
  walkNodeTree(next.body, (n) => {
    if (n.kind !== 'table') return;
    if (!(n.repeatRegions ?? []).some((r) => r.over === dsKey)) return;
    if (n.cells.some((c) => c.binding?.bindingKey === field)) tableIds.push(n.id);
  });
  for (const tableId of tableIds) next = clearFieldCellBinding(next, tableId, field);

  // b) 선언 fields에서 제거.
  const dsIndex = findDataSourceIndex(next, dsKey);
  if (dsIndex !== -1) {
    const dataSources = next.dataSources ?? [];
    if ((dataSources[dsIndex].fields ?? []).some((f) => f.field === field)) {
      next = {
        ...next,
        dataSources: dataSources.map((d, i) =>
          i === dsIndex ? { ...d, fields: (d.fields ?? []).filter((f) => f.field !== field) } : d,
        ),
      };
    }
  }
  return next;
}

/**
 * 이 소스를 반복하는 표에서 그 필드가 배치된 셀 수 — 삭제 확인 문구("표 N곳")에 쓴다.
 * 삭제 op와 **같은 탐색 규칙**을 써야 안내와 실제 파급이 어긋나지 않는다.
 */
export function countDataSourceFieldCells(t: ReportTemplate, dsKey: string, field: string): number {
  let count = 0;
  walkNodeTree(t.body, (n) => {
    if (n.kind !== 'table') return;
    if (!(n.repeatRegions ?? []).some((r) => r.over === dsKey)) return;
    for (const c of n.cells) if (c.binding?.bindingKey === field) count++;
  });
  return count;
}

/**
 * (칼럼 ↔ 셀 매핑 해제) 이 필드에 매핑된 셀의 바인딩만 푼다 — **선언은 건드리지 않는다**.
 *
 * 삭제(deleteDataSourceColumn)와 나누는 이유: 삭제는 선언 fields에서 필드를 지우는데, 조립형
 * (`kind:'assembled'`) 필드의 `from`(예 'primary.locationName')은 **이 패널에 보이지 않는다**
 * — 그 칸은 전역 속성의 데이터 소스 패널에 있다. 그래서 매핑만 풀 생각으로 삭제를 누르면
 * `＋칼럼 추가`로 되돌려도 `from: ''`인 빈 필드가 되고, 어디서 다시 이어야 하는지 알기 어렵다
 * (2026-08-11 사용자 제보). 해제한 필드는 목록에 '미지정'으로 남아 곧바로 다시 '셀 지정'할 수
 * 있다 — 선언을 건드리지 않으므로 잃는 것이 없다.
 *
 * 매핑된 셀이 없으면 원본 참조 그대로(no-op 히스토리 방지).
 */
export function unmapDataSourceColumn(
  t: ReportTemplate,
  params: { tableId: string; field: string },
): ReportTemplate {
  return clearFieldCellBinding(t, params.tableId, params.field);
}

/**
 * 매핑된 셀의 바인딩 해제 — 해제/삭제가 공유하는 1단계.
 * 특례 자동 생성 콘텐츠(단일 image/choicegroup + 같은 binding)만 함께 비운다 — 잔존 시
 * content>binding 우선이라 유령으로 남는다(손저작 콘텐츠는 보존).
 *
 * ⚠️ 한 표에 같은 필드가 **여러 셀**에 배치될 수 있다(다중 행 반복영역 밴드). 예전엔 `find`로
 * 첫 셀만 풀어, 없애려던 유령 바인딩이 나머지 셀에 그대로 남았다. 안내 문구가 쓰는
 * `countDataSourceFieldCells`와 rename(`renameDataSourceFieldEverywhere`)은 전 셀을 처리하므로
 * 여기만 단수면 "파급 범위 동일"이라는 규약이 깨진다.
 */
function clearFieldCellBinding(
  t: ReportTemplate,
  tableId: string,
  field: string,
): ReportTemplate {
  const table = findNodeById(t.body, tableId);
  if (table?.kind !== 'table') return t;
  const cells = table.cells.filter((c) => c.binding?.bindingKey === field);
  if (cells.length === 0) return t;
  // 좌표로 갱신하므로 매 회 최신 트리를 다시 읽는다(updateCell은 새 트리를 반환).
  return cells.reduce(
    (acc, cell) =>
      updateCell(acc, tableId, cell.row, cell.col, {
        binding: undefined,
        ...(isAutoFieldCellContent(cell.content, field) ? { content: [] } : {}),
      }),
    t,
  );
}

/**
 * (dataSource 칼럼 삭제) 이 선언(dsKey)에 매핑된 셀의 바인딩을 해제하고 선언 fields에서 필드를
 * 제거한다(한 번에). 셀에 매핑되지 않은 필드는 fields 제거만 수행.
 *
 * ⚠️ 매핑만 풀려면 unmapDataSourceColumn을 쓸 것 — 이 함수는 조립형 소스의 `from` 연결까지
 * 없애며 그건 UI로 되돌릴 수 없다.
 */
export function deleteDataSourceColumn(
  t: ReportTemplate,
  params: { tableId: string; dsKey: string; field: string },
): ReportTemplate {
  const { tableId, dsKey, field } = params;
  // 1) 셀 바인딩 해제(매핑된 셀 있으면).
  let next = clearFieldCellBinding(t, tableId, field);
  // 2) 선언 fields에서 제거.
  const dsIndex = findDataSourceIndex(next, dsKey);
  if (dsIndex !== -1) {
    const dataSources = next.dataSources ?? [];
    next = {
      ...next,
      dataSources: dataSources.map((d, i) =>
        i === dsIndex ? { ...d, fields: (d.fields ?? []).filter((f) => f.field !== field) } : d,
      ),
    };
  }
  return next;
}

/**
 * (dataSource 칼럼 순서 이동) 선언(dsKey) fields에서 field를 dir(-1=위/+1=아래)로 한 칸 이동한다.
 * 표의 칼럼 위치(셀 좌표)는 건드리지 않는다 — 순서는 칼럼 목록·작성폼 입력 표시 순서에만 영향.
 * (기본은 추가 순서 유지, 필요 시 사용자가 조정 — 2026-07-04 사용자 확정.)
 * 경계 밖/필드 없음/dsKey 부재면 원본 참조 그대로 반환(no-op 히스토리 방지).
 */
export function moveDataSourceColumn(
  t: ReportTemplate,
  params: { dsKey: string; field: string; dir: -1 | 1 },
): ReportTemplate {
  const { dsKey, field, dir } = params;
  const dsIndex = findDataSourceIndex(t, dsKey);
  if (dsIndex === -1) return t;
  const dataSources = t.dataSources ?? [];
  const fields = dataSources[dsIndex].fields ?? [];
  const idx = fields.findIndex((f) => f.field === field);
  if (idx === -1) return t;
  const to = idx + dir;
  if (to < 0 || to >= fields.length) return t;
  const nextFields = fields.slice();
  [nextFields[idx], nextFields[to]] = [nextFields[to], nextFields[idx]];
  return {
    ...t,
    dataSources: dataSources.map((d, i) => (i === dsIndex ? { ...d, fields: nextFields } : d)),
  };
}
